import type { Room } from '../types';

export type PlanPoint = [number, number];

export const rectangularOutline = (width: number, length: number): PlanPoint[] => [
  [-width / 2, -length / 2], [width / 2, -length / 2], [width / 2, length / 2], [-width / 2, length / 2],
];

export const roomOutline = (room: Room): PlanPoint[] => room.outline?.length ? room.outline : rectangularOutline(room.width, room.length);

export function outlineBounds(points: PlanPoint[]) {
  const xs = points.map((point) => point[0]), zs = points.map((point) => point[1]);
  const minX = Math.min(...xs), maxX = Math.max(...xs), minZ = Math.min(...zs), maxZ = Math.max(...zs);
  return { minX, maxX, minZ, maxZ, width: maxX - minX, length: maxZ - minZ };
}

const cross = (a: PlanPoint, b: PlanPoint, c: PlanPoint) => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
const onSegment = (a: PlanPoint, b: PlanPoint, c: PlanPoint) => Math.min(a[0], b[0]) <= c[0] && c[0] <= Math.max(a[0], b[0]) && Math.min(a[1], b[1]) <= c[1] && c[1] <= Math.max(a[1], b[1]);
function intersects(a: PlanPoint, b: PlanPoint, c: PlanPoint, d: PlanPoint) {
  const abC = cross(a, b, c), abD = cross(a, b, d), cdA = cross(c, d, a), cdB = cross(c, d, b);
  if (abC === 0 && onSegment(a, b, c)) return true;
  if (abD === 0 && onSegment(a, b, d)) return true;
  if (cdA === 0 && onSegment(c, d, a)) return true;
  if (cdB === 0 && onSegment(c, d, b)) return true;
  return abC * abD < 0 && cdA * cdB < 0;
}

export function validRoomOutline(points: PlanPoint[]): boolean {
  if (points.length < 3 || points.length > 200 || !points.every((point) => point.length === 2 && point.every(Number.isFinite))) return false;
  const bounds = outlineBounds(points);
  if (bounds.width < 100 || bounds.length < 100) return false;
  const area = points.reduce((sum, point, index) => sum + cross([0, 0], point, points[(index + 1) % points.length]), 0);
  if (Math.abs(area) < 10000) return false;
  for (let index = 0; index < points.length; index += 1) {
    if (Math.hypot(points[index][0] - points[(index + 1) % points.length][0], points[index][1] - points[(index + 1) % points.length][1]) < 100) return false;
    for (let other = index + 2; other < points.length; other += 1) {
      if (index === 0 && other === points.length - 1) continue;
      if (intersects(points[index], points[(index + 1) % points.length], points[other], points[(other + 1) % points.length])) return false;
    }
  }
  return true;
}

export function roomWithPatch(room: Room, patch: Partial<Room>): Room {
  const next = { ...room, ...patch };
  if (Object.prototype.hasOwnProperty.call(patch, 'outline') && patch.outline === undefined) return { ...next, outline: undefined };
  if (patch.outline?.length) {
    const bounds = outlineBounds(patch.outline);
    return { ...next, width: bounds.width, length: bounds.length };
  }
  if (room.outline && (patch.width !== undefined || patch.length !== undefined)) {
    const bounds = outlineBounds(room.outline);
    const centerX = (bounds.minX + bounds.maxX) / 2, centerZ = (bounds.minZ + bounds.maxZ) / 2;
    const width = patch.width ?? room.width, length = patch.length ?? room.length;
    return { ...next, outline: room.outline.map(([x, z]) => [centerX + (x - centerX) * width / bounds.width, centerZ + (z - centerZ) * length / bounds.length]) };
  }
  return next;
}
